import { useCallback, useEffect, useMemo, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { Plus, Search, CalendarClock, MapPin, User, Image as ImageIcon, MessageSquareShare, Truck } from "lucide-react";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { useAuth } from "@/context/AuthContext";
import { can } from "@/lib/roles";
import { toast } from "sonner";
import { Input } from "@/components/ui/input";
import { api, eur, formatApiError } from "@/lib/api";
import { NewAppointmentDialog } from "@/components/NewAppointmentDialog";
import { StatusBadge } from "@/components/StatusBadge";
import { WhatsAppDay } from "@/components/WhatsAppDay";

function AppointmentCard({ a }) {
  return (
    <Link to={`/appuntamenti/${a.id}`} data-testid={`appointment-card-${a.id}`} className="card-lift block rounded-2xl bg-white border border-[var(--border)] p-5">
      <div className="flex items-start justify-between gap-3 mb-3">
        <p className="font-heading font-semibold text-lg leading-tight">{a.client_name}</p>
        <StatusBadge status={a.status} />
      </div>
      <div className="space-y-1.5 text-sm text-[var(--muted)]">
        <p className="flex items-center gap-2"><CalendarClock className="h-4 w-4" /> {new Date(a.date + "T00:00").toLocaleDateString("it-IT", { weekday: "short", day: "numeric", month: "short" })} {a.time && `· ${a.time}`}</p>
        {"pickup_date" in a && <p className={`flex items-center gap-2 ${a.pickup_date ? "" : "text-amber-700"}`} data-testid={`appointment-pickup-${a.id}`}><Truck className="h-4 w-4 shrink-0" /> {a.pickup_date ? `Ritiro ${new Date(a.pickup_date + "T00:00").toLocaleDateString("it-IT", { weekday: "short", day: "numeric", month: "short" })}${a.pickup_time ? ` · ${a.pickup_time}` : ""}` : "Ritiro da concordare"}</p>}
        {a.client_address && <p className="flex items-center gap-2 truncate"><MapPin className="h-4 w-4 shrink-0" /> {a.client_address}</p>}
        <p className="flex items-center gap-2 truncate" data-testid={`appointment-assignee-${a.id}`}><User className="h-4 w-4 shrink-0" /> {a.assigned_name || "Non assegnato"}</p>
      </div>
      <div className="mt-4 pt-4 border-t border-dashed border-[var(--border)] flex items-end justify-between">
        <span className="flex items-center gap-1.5 text-xs text-[var(--muted)]"><ImageIcon className="h-3.5 w-3.5" /> {a.items.length} oggetti</span>
        <span className="font-mono font-semibold">{eur(a.totals.items_total)}</span>
      </div>
    </Link>
  );
}

export default function Appointments() {
  const [list, setList] = useState([]);
  const [q, setQ] = useState("");
  const [filter, setFilter] = useState("tutti");
  const [open, setOpen] = useState(false);
  const [waOpen, setWaOpen] = useState(false);
  const navigate = useNavigate();
  const { user } = useAuth();

  const load = useCallback(async () => {
    try {
      const { data } = await api.get("/appointments");
      setList(data);
    } catch (e) {
      toast.error(formatApiError(e));
    }
  }, []);

  useEffect(() => {
    load();
    const t = setInterval(load, 20000);
    return () => clearInterval(t);
  }, [load]);

  const shown = useMemo(() => list.filter((a) =>
    (filter === "tutti" || (filter === "miei" ? a.assigned_to === user.id : a.status === filter)) &&
    (a.client_name + " " + a.client_address).toLowerCase().includes(q.toLowerCase())
  ), [list, q, filter, user.id]);

  return (
    <div className="space-y-8">
      <div className="flex flex-col sm:flex-row sm:items-end justify-between gap-4 fade-up">
        <div>
          <p className="eyebrow">Preventivi</p>
          <h1 className="font-heading text-3xl sm:text-4xl font-bold tracking-tight">Appuntamenti</h1>
        </div>
        <div className="flex gap-2">
          <button className="btn-secondary h-11 w-11 p-0" onClick={() => setWaOpen(true)} aria-label="Riepilogo WhatsApp" title="Riepilogo WhatsApp" data-testid="open-whatsapp-day-button"><MessageSquareShare className="h-5 w-5 text-emerald-700" /></button>
          {can(user, "appt_write") && <button className="btn-primary h-11 px-5" onClick={() => setOpen(true)} data-testid="new-appointment-button"><Plus className="h-5 w-5" /> Nuovo appuntamento</button>}
        </div>
      </div>
      <div className="flex flex-col sm:flex-row gap-3">
        <div className="relative flex-1">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-[var(--muted)]" />
          <Input placeholder="Cerca cliente o indirizzo" value={q} onChange={(e) => setQ(e.target.value)} className="pl-9 h-11 bg-white" data-testid="appointments-search-input" />
        </div>
        <div className="flex gap-2 overflow-x-auto">
          {[["tutti", "Tutti"], ["miei", "Assegnati a me"], ["aperto", "Aperti"], ["firmato", "Firmati"]].map(([k, l]) => (
            <button key={k} onClick={() => setFilter(k)} data-testid={`filter-${k}-button`} className={`chip ${filter === k ? "chip-active" : ""}`}>{l}</button>
          ))}
        </div>
      </div>
      {shown.length === 0 ? (
        <div className="rounded-2xl border border-dashed border-[var(--border)] p-12 text-center text-[var(--muted)]" data-testid="appointments-empty">
          {filter === "tutti" && !q ? "Nessun appuntamento. Crea il primo con “Nuovo appuntamento”." : "Nessun appuntamento corrisponde al filtro selezionato."}
        </div>
      ) : (
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4" data-testid="appointments-list">
          {shown.map((a) => <AppointmentCard key={a.id} a={a} />)}
        </div>
      )}
      <Dialog open={waOpen} onOpenChange={setWaOpen}>
        <DialogContent className="max-w-lg bg-white" data-testid="whatsapp-day-dialog">
          <DialogHeader><DialogTitle className="font-heading text-xl">Riepilogo WhatsApp</DialogTitle></DialogHeader>
          <WhatsAppDay appointments={list} />
        </DialogContent>
      </Dialog>
      <NewAppointmentDialog open={open} onOpenChange={setOpen} onCreated={(a) => navigate(`/appuntamenti/${a.id}`)} />
    </div>
  );
}
